import { useCallback, useEffect, useRef, useState } from "react";

export type CellSaveStatus = "idle" | "saving" | "saved" | "error";

type SaveResult = { success?: boolean } | null | undefined;
type SaveOperation = () => Promise<SaveResult> | SaveResult;

export function useCellSaveState(savedDuration = 1200) {
  const [statuses, setStatuses] = useState<Record<string, CellSaveStatus>>({});
  const queues = useRef<Record<string, Promise<SaveResult>>>({});
  const timers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;

    return () => {
      mounted.current = false;
      Object.values(timers.current).forEach(clearTimeout);
    };
  }, []);

  const setStatus = useCallback((key: string, status: CellSaveStatus) => {
    if (!mounted.current) return;
    setStatuses((current) => ({ ...current, [key]: status }));
  }, []);

  const saveCell = useCallback(
    (key: string, operation: SaveOperation) => {
      if (timers.current[key]) {
        clearTimeout(timers.current[key]);
        delete timers.current[key];
      }

      setStatus(key, "saving");

      const previous = queues.current[key] ?? Promise.resolve(null);
      const task = previous
        .catch(() => null)
        .then(operation)
        .catch(() => ({ success: false }));

      queues.current[key] = task;

      task.then((result) => {
        if (!mounted.current || queues.current[key] !== task) return;

        delete queues.current[key];
        if (!result?.success) {
          setStatus(key, "error");
          return;
        }

        setStatus(key, "saved");
        timers.current[key] = setTimeout(() => {
          if (!mounted.current) return;
          setStatuses((current) => {
            if (current[key] !== "saved") return current;

            const next = { ...current };
            delete next[key];
            return next;
          });
          delete timers.current[key];
        }, savedDuration);
      });

      return task;
    },
    [savedDuration, setStatus],
  );

  const getSaveStatus = useCallback(
    (key: string): CellSaveStatus => statuses[key] ?? "idle",
    [statuses],
  );

  return { getSaveStatus, saveCell };
}
